import useLocale from 'next-translate/useTranslation';
import { useEffect, useState } from 'react';
import { Audio } from 'types';

import { convertToHumanReadableSeconds } from '@/lib/convertTranscriptionTimestamp';

import axiosInstance from '@/utils/httpClient';

import TranscriptionWithSpeakersSegment from './TranscriptionWithSpeakersSegment';

interface TranscriptionWithSpeakersProps {
  localAudio: Audio;
}

const Transcriptions = ({ localAudio }: TranscriptionWithSpeakersProps) => {
  const { t } = useLocale('transcription');

  const [speakerNames, setSpeakerNames] = useState<Record<string, string>>(
    localAudio.speaker_names || {}
  );
  const [uniqueSpeakers, setUniqueSpeakers] = useState<Set<string>>(new Set());

  useEffect(() => {
    const speakers: Set<string> = new Set();

    localAudio.segments &&
      localAudio.segments.forEach((segment) => {
        if ('speaker' in segment) {
          speakers.add(`Speaker ${segment.speaker}`);
        }
      });

    setUniqueSpeakers(speakers);
  }, [localAudio]);

  const handleSpeakerNameChange = (speaker: string, newName: string) => {
    setSpeakerNames({
      ...speakerNames,
      [speaker]: newName,
    });
  };

  const saveSpeakerNames = async () => {
    // Save the speaker names to the database.
    // You need to implement this function based on your back-end setup.

    await axiosInstance.post('/api/save-speaker-names', {
      params: speakerNames,
      uuid: localAudio.uuid,
    });
    localAudio.speaker_names = speakerNames;
  };

  const markdownTranscriptionWithSpeakers: string = !localAudio.segments
    ? ''
    : localAudio.segments
        .map((segment, index) => {
          if ('speaker' in segment) {
            const speakerKey = `Speaker ${segment.speaker}`;

            if (segment.text) {
              return `**${speakerNames[speakerKey] || t(speakerKey)}**:\n${
                segment.text
              }`;
            }

            if (!segment.transcript || segment.transcript.length === 0) {
              return;
            }

            return `**${
              speakerNames[speakerKey] || t(speakerKey)
            }**:\n${segment.transcript
              .map((transcript) => transcript.text)
              .join(' ')}`;
          }
        })
        .join('\n');
  return (
    <div className='text-left'>
      {localAudio.segments && (
        <div className='mt-10'>
          <h2 className='mb-10 text-2xl font-bold text-primary-600'>
            Transcript
          </h2>
          <div className='mt-1 text-left'>
            {localAudio.segments.map((segment, index) => {
              if ('speaker' in segment) {
                const speakerKey = `Speaker ${segment.speaker}`;

                if (segment.text) {
                  return (
                    <TranscriptionWithSpeakersSegment
                      key={index}
                      segment={segment}
                      speakerKey={speakerNames[speakerKey] || t(speakerKey)}
                    />
                  );
                }

                if (!segment.transcript || segment.transcript.length === 0) {
                  return;
                }

                return (
                  <div
                    key={index}
                    className='flex flex-col py-2 md:flex-row md:space-x-4'
                  >
                    <div className='w-full md:w-1/4'>
                      <span className='block font-bold dark:text-slate-400'>
                        {speakerNames[speakerKey] || t(speakerKey)}
                      </span>
                      <span className='text-xs text-gray-400 dark:text-slate-600'>
                        {convertToHumanReadableSeconds(segment.start)} -{' '}
                        {convertToHumanReadableSeconds(segment.stop)}
                      </span>
                    </div>
                    <div className='mt-2 w-full md:mt-0'>
                      {segment.transcript.map((transcript, transcriptIndex) => (
                        <span
                          key={transcriptIndex}
                          className='dark:text-slate-400'
                        >
                          {transcript.text}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Transcriptions;
